/**
 * MyCourses (Sprint 4 B5) — page "Môn học của tôi".
 *
 * Layout:
 *   - Breadcrumb
 *   - Tabs filter: Tất cả / Đang học / Đã hoàn thành / Đã bỏ
 *   - Grid course card với status badge
 *   - Empty state cho từng tab
 */

import { useState } from "react";
import {
  Breadcrumb,
  Card,
  Col,
  Empty,
  Result,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useMyEnrollments, type EnrollmentStatus } from "@/api/enrollment";
import { TeachingCourses } from "@/components/TeachingCourses";
import { useAuthStore } from "@/stores/auth";

const { Title, Text, Paragraph } = Typography;

type Enrollment = NonNullable<ReturnType<typeof useMyEnrollments>["data"]>[number];

const NO_TERM_KEY = "no-term";

interface TermGroup {
  key: string;
  name: string;
  startsAt: string | null;
  endsAt: string | null;
  items: Enrollment[];
}

/** Nhóm lượt ghi danh theo kỳ học — kỳ mới nhất trước, "Không gắn kỳ" cuối (2026-10-01). */
function groupByTerm(enrollments: Enrollment[]): TermGroup[] {
  const map = new Map<string, TermGroup>();
  for (const e of enrollments) {
    const key = e.term ? String(e.term.id) : NO_TERM_KEY;
    if (!map.has(key)) {
      map.set(key, {
        key,
        name: e.term?.name ?? "Không gắn kỳ học",
        startsAt: e.term?.starts_at ?? null,
        endsAt: e.term?.ends_at ?? null,
        items: [],
      });
    }
    map.get(key)!.items.push(e);
  }
  for (const g of map.values()) g.items.sort((a, b) => a.course.code.localeCompare(b.course.code));
  return [...map.values()].sort((a, b) => {
    if (a.key === NO_TERM_KEY) return 1;
    if (b.key === NO_TERM_KEY) return -1;
    return (b.startsAt ?? "").localeCompare(a.startsAt ?? "");
  });
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const STATUS_META: Record<
  EnrollmentStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  enrolled: { label: "Đang học", color: "blue", icon: <ClockCircleOutlined /> },
  completed: { label: "Đã hoàn thành", color: "gold", icon: <CheckCircleOutlined /> },
  dropped: { label: "Đã bỏ", color: "default", icon: <StopOutlined /> },
  expired: { label: "Hết hạn", color: "red", icon: <StopOutlined /> },
};

export function MyCourses() {
  const { data: enrollments, isLoading, error } = useMyEnrollments();
  const [tab, setTab] = useState<string | undefined>();
  const today = todayIso();
  const groups = groupByTerm(enrollments ?? []);
  // Mặc định: kỳ đang diễn ra; không có thì kỳ mới nhất.
  const currentKey = groups.find(
    (g) => g.startsAt && g.startsAt <= today && (!g.endsAt || g.endsAt >= today),
  )?.key;
  const activeKey = tab ?? currentKey ?? groups[0]?.key;
  // Giảng viên không ghi danh môn nào — hiện các môn đang phụ trách (bug 2026-10-01).
  const isInstructor = useAuthStore((st) => st.user?.role) === "instructor";

  if (isInstructor) {
    return (
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            {
              title: (
                <Link to="/">
                  <HomeOutlined />
                </Link>
              ),
            },
            { title: "Môn học của tôi" },
          ]}
        />
        <Title level={2}>Môn học của tôi</Title>
        <Paragraph type="secondary">Các môn học bạn đang được phân công phụ trách.</Paragraph>
        <TeachingCourses />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: "Môn học của tôi" },
        ]}
      />

      <Title level={2}>Môn học của tôi</Title>

      {isLoading && (
        <div style={{ textAlign: "center", padding: 64 }}>
          <Spin size="large" />
        </div>
      )}

      {error && <Result status="error" title="Không tải được danh sách" subTitle={String(error)} />}

      {!isLoading && enrollments && enrollments.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Bạn chưa đăng ký môn học nào. Hãy khám phá thư viện môn học."
          style={{ padding: 48 }}
        >
          <Link to="/study-plan">
            <Tag color="blue" style={{ fontSize: 14, padding: "6px 12px" }}>
              Kế hoạch học tập →
            </Tag>
          </Link>
        </Empty>
      )}

      {!isLoading && groups.length > 0 && (
        <Tabs
          activeKey={activeKey}
          onChange={setTab}
          data-testid="my-courses-term-tabs"
          items={groups.map((g) => {
            const isPast = !!g.endsAt && g.endsAt < today;
            const isCurrent = g.key === currentKey;
            return {
              key: g.key,
              label: (
                <span>
                  {g.name} ({g.items.length}) {isCurrent && <Tag color="blue">Kỳ hiện tại</Tag>}
                  {isPast && !isCurrent && <Tag>Đã kết thúc</Tag>}
                </span>
              ),
              children: (
                <Row gutter={[16, 16]} data-testid="enrollments-grid">
                  {g.items.map((enr) => {
                    const meta = STATUS_META[enr.status];
                    return (
                      <Col xs={24} sm={12} lg={8} key={enr.id}>
                        <Link
                          to={`/my-courses/${enr.course.slug}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Card
                            hoverable
                            cover={
                              enr.course.cover_image ? (
                                <img
                                  src={enr.course.cover_image}
                                  alt={enr.course.name}
                                  style={{ height: 140, objectFit: "cover" }}
                                />
                              ) : (
                                <div
                                  style={{
                                    height: 140,
                                    background: "linear-gradient(135deg,#1677ff,#5e8df5)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <BookOutlined style={{ fontSize: 36, color: "#fff" }} />
                                </div>
                              )
                            }
                          >
                            <Space direction="vertical" size={4} style={{ width: "100%" }}>
                              <Tag color="blue">{enr.course.code}</Tag>
                              <Text strong style={{ fontSize: 16 }}>
                                {enr.course.name}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {enr.course.term_names.length > 0
                                  ? `${enr.course.term_names.join(", ")} · `
                                  : ""}
                                {enr.course.lesson_count} bài
                              </Text>
                              <Tag icon={meta.icon} color={meta.color}>
                                {meta.label}
                              </Tag>
                            </Space>
                          </Card>
                        </Link>
                      </Col>
                    );
                  })}
                </Row>
              ),
            };
          })}
        />
      )}
    </div>
  );
}
